import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Shield, User, Briefcase, Search, ChevronDown, MoreVertical, Trash2, Edit, Calendar, GitMerge, Loader2, RefreshCw, Settings, MessageCircle, Mail, CheckCircle2, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth, api } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { toast } from 'sonner';

// ============ ACOMPANHAMENTO DE CLIENTES — MOBILE FIRST ============
// Integração isolada: não cria marcações nem envia emails/WhatsApp automaticamente.
const returnDate = (value, language) => {
  if (!value) return '—';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00Z`) : new Date(value);
  return isNaN(date.getTime()) ? '—' : date.toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-GB', { timeZone: 'Europe/Lisbon', day: '2-digit', month: 'short', year: 'numeric' });
};
const validContactEmail = value => Boolean(value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && !value.includes('@temp.lousway'));
const waPhone = value => {
  const digits = (value || '').replace(/\D/g, '');
  return digits.length === 9 && digits[0] === '9' ? `351${digits}` : (digits.length >= 10 && digits.length <= 15 ? digits : '');
};

const CONTACT_PENDING_KEY = 'lousway_retention_external_contact_v1';
const contactMethodLabel = (method, language) => method === 'whatsapp' ? 'WhatsApp' : method === 'email' ? 'Email' : (language === 'pt' ? 'Outro' : 'Other');

const ClientRetentionPanel = ({ language, onViewHistory, onEditClient, onUserDeleted, isAdmin }) => {
  const tr = (pt, en) => language === 'pt' ? pt : en;
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(true);
  const [reportError, setReportError] = useState('');
  const [filter, setFilter] = useState('attention');
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(40);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [ruleQuery, setRuleQuery] = useState('');
  const [ruleDraft, setRuleDraft] = useState({});
  const [inactiveDraft, setInactiveDraft] = useState('90');
  const [saving, setSaving] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [contactTarget, setContactTarget] = useState(null);
  const [contactMethod, setContactMethod] = useState('other');
  const [contactSaving, setContactSaving] = useState(false);
  const pendingContactRef = useRef(null);

  const forgetPendingContact = () => {
    pendingContactRef.current = null;
    try { window.sessionStorage.removeItem(CONTACT_PENDING_KEY); } catch (_) { /* storage unavailable */ }
  };

  const closeContactDialog = () => {
    setContactTarget(null);
    forgetPendingContact();
  };

  // O link abre outra aplicação. Perguntamos AO REGRESSAR, mas só registamos
  // contacto depois da confirmação humana, nunca no clique no link.
  const startExternalContact = (client, method) => {
    const pending = { user_id: client.user_id, name: client.name, method, started: Date.now() };
    pendingContactRef.current = pending;
    try { window.sessionStorage.setItem(CONTACT_PENDING_KEY, JSON.stringify(pending)); } catch (_) { /* optional */ }
  };

  useEffect(() => {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(CONTACT_PENDING_KEY) || 'null');
      if (stored && stored.user_id && stored.name && stored.method &&
          Date.now() - Number(stored.started) < 24 * 60 * 60 * 1000) {
        pendingContactRef.current = stored;
        // Se o separador foi recarregado, a pergunta não se perde.
        if (Date.now() - Number(stored.started) > 1200) {
          setContactMethod(stored.method);
          setContactTarget({ user_id: stored.user_id, name: stored.name, mode: 'after-link' });
        }
      } else {
        window.sessionStorage.removeItem(CONTACT_PENDING_KEY);
      }
    } catch (_) { /* Safari modo privado ou dados inválidos */ }
    const askOnReturn = () => {
      const pending = pendingContactRef.current;
      if (!pending || document.visibilityState === 'hidden') return;
      if (Date.now() - Number(pending.started) < 750) return;
      setContactMethod(pending.method);
      setContactTarget({ user_id: pending.user_id, name: pending.name, mode: 'after-link' });
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') askOnReturn(); };
    window.addEventListener('focus', askOnReturn);
    window.addEventListener('pageshow', askOnReturn); // iPhone Safari / retrocesso do histórico
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', askOnReturn);
      window.removeEventListener('pageshow', askOnReturn);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const promptManualContact = (client) => {
    forgetPendingContact();
    setContactMethod('other');
    setContactTarget({ user_id: client.user_id, name: client.name, mode: 'manual' });
  };

  const saveContactStatus = async (clientId, contacted, method = 'other') => {
    if (contactSaving) return;
    try {
      setContactSaving(true);
      await api.put(`/admin/retention/contacts/${encodeURIComponent(clientId)}`,
                    { contacted, method });
      toast.success(contacted ? tr('Cliente marcada como contactada.', 'Client marked as contacted.') :
                 tr('Cliente voltou à lista de acompanhamento.', 'Client returned to the follow-up list.'));
      closeContactDialog();
      await refreshReport();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr('Não foi possível guardar o contacto.', 'Could not save contact.'));
    } finally {
      setContactSaving(false);
    }
  };

  const refreshReport = async () => {
    setLoadingReport(true);
    setReportError('');
    try {
      const response = await api.get('/admin/retention/overview');
      const next = response.data;
      setReport(next);
      setInactiveDraft(String(next.inactivity_days));
      setRuleDraft(Object.fromEntries((next.service_rules || []).map(s => [s.service_id, s.return_days == null ? '' : String(s.return_days)])));
    } catch (e) {
      console.error('Retention data load failed:', e);
      setReportError(e?.response?.data?.detail || tr('Não foi possível carregar a análise de clientes.', 'Could not load client insights.'));
    } finally {
      setLoadingReport(false);
    }
  };
  useEffect(() => { refreshReport(); }, []); // apenas ao abrir a secção
  useEffect(() => { setLimit(40); }, [filter, query]);

  const confirmRetentionDelete = async () => {
    if (!isAdmin || !deleteTarget || deleting || deleteTarget.total_bookings > 0) return;
    const target = deleteTarget;
    try {
      setDeleting(true);
      await api.delete(`/admin/users/${encodeURIComponent(target.user_id)}`);
      setDeleteTarget(null);
      toast.success(tr('Utilizador excluído com sucesso.', 'User deleted successfully.'));
      // Atualizar a aba atual e a lista geral sem mudar de separador.
      await Promise.all([refreshReport(), Promise.resolve(onUserDeleted?.())]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || tr('Não foi possível excluir este utilizador.', 'Could not delete this user.'));
    } finally {
      setDeleting(false);
    }
  };

  const saveRule = async (service) => {
    if (!isAdmin) return;
    const raw = (ruleDraft[service.service_id] ?? '').trim();
    if (raw && (!/^\d+$/.test(raw) || Number(raw) < 1 || Number(raw) > 730)) {
      toast.error(tr('Indica entre 1 e 730 dias, ou deixa vazio para desativar.', 'Enter 1–730 days or leave empty to disable.'));
      return;
    }
    const newDays = raw ? Number(raw) : null;
    if (newDays === null && service.return_days != null && !window.confirm(tr('Retirar o prazo de regresso deste serviço?', 'Remove this service return deadline?'))) return;
    try {
      setSaving(service.service_id);
      await api.put(`/admin/retention/services/${encodeURIComponent(service.service_id)}`, { return_days: newDays });
      toast.success(tr('Prazo do serviço guardado.', 'Return deadline saved.'));
      await refreshReport();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr('Erro ao guardar prazo.', 'Could not save deadline.'));
    } finally { setSaving(''); }
  };
  const saveInactivity = async () => {
    if (!isAdmin) return;
    if (!/^\d+$/.test(inactiveDraft) || Number(inactiveDraft) < 1 || Number(inactiveDraft) > 730) {
      toast.error(tr('Indica um prazo entre 1 e 730 dias.', 'Choose a period from 1 to 730 days.'));
      return;
    }
    try {
      setSaving('inactivity');
      await api.put('/admin/retention/inactivity', { inactivity_days: Number(inactiveDraft) });
      toast.success(tr('Prazo de inatividade guardado.', 'Inactivity period saved.'));
      await refreshReport();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr('Erro ao guardar.', 'Could not save.'));
    } finally { setSaving(''); }
  };

  if (loadingReport && !report) return (
    <div className="bg-white rounded-2xl p-8 text-center border border-[#E6E2DA]" aria-live="polite">
      <Loader2 className="w-7 h-7 mx-auto mb-3 text-[#BFA05F] animate-spin" />
      <p className="text-sm text-[#635E54]">{tr('A analisar as marcações das clientes…', 'Analysing customer bookings…')}</p>
    </div>
  );
  if (!report) return (
    <div className="bg-white border border-red-200 rounded-2xl p-5 space-y-4">
      <p className="text-sm text-red-700" role="alert">{reportError}</p>
      <Button onClick={refreshReport} variant="outline" className="h-11 rounded-full">{tr('Tentar novamente', 'Try again')}</Button>
    </div>
  );

  const stats = report.stats || {};
  const matches = (report.clients || []).filter(c => {
    const digits = (query || '').replace(/\D/g, '');
    const textMatch = !query || [c.name, c.email, c.phone].some(s => (s || '').toLowerCase().includes(query.trim().toLowerCase())) || (digits.length >= 3 && (c.phone || '').replace(/\D/g, '').includes(digits));
    if (!textMatch) return false;
    const contacted = Boolean(c.contact?.contacted);
    if (filter === 'contacted') return contacted;
    if (filter === 'all') return true;
    if (contacted) return false;
    if (filter === 'attention') return c.never_booked || c.without_visit || c.inactive || (c.overdue || []).length > 0;
    if (filter === 'overdue') return (c.overdue || []).length > 0;
    if (filter === 'inactive') return c.inactive;
    if (filter === 'never') return c.never_booked;
    if (filter === 'without_visit') return c.without_visit;
    return true;
  });
  matches.sort((a, b) => {
    if (filter === 'contacted') return (b.contact?.contacted_at || '').localeCompare(a.contact?.contacted_at || '');
    const risk = c => (c.overdue || []).length ? 5 : c.inactive ? 4 : c.never_booked ? 3 : c.without_visit ? 2 : 0;
    const score = risk(b) - risk(a);
    if (score) return score;
    return (b.overdue?.[0]?.days_overdue || b.days_since_last_booking || 0) - (a.overdue?.[0]?.days_overdue || a.days_since_last_booking || 0);
  });
  const filters = [
    ['attention', tr('A contactar', 'Follow up'), (report.clients || []).filter(c => !c.contact?.contacted && (c.never_booked || c.without_visit || c.inactive || c.overdue?.length)).length],
    ['contacted', tr('Já contactadas', 'Contacted'), stats.contacted || 0],
    ['overdue', tr('Prazo ultrapassado', 'Overdue'), (report.clients || []).filter(c => !c.contact?.contacted && c.overdue?.length).length],
    ['inactive', tr('Inativos', 'Inactive'), (report.clients || []).filter(c => !c.contact?.contacted && c.inactive).length],
    ['never', tr('Nunca marcaram', 'Never booked'), (report.clients || []).filter(c => !c.contact?.contacted && c.never_booked).length],
    ['without_visit', tr('Sem visita anterior', 'No past visit'), (report.clients || []).filter(c => !c.contact?.contacted && c.without_visit).length],
    ['all', tr('Todos', 'All'), stats.total_clients || 0],
  ];
  const visibleRules = (report.service_rules || []).filter(s => (s.name_pt || '').toLowerCase().includes(ruleQuery.toLowerCase()));

  return (
    <section className="space-y-4" data-testid="retention-panel" style={{ paddingBottom: 'max(28px, env(safe-area-inset-bottom, 0px))' }}>
      <div className="rounded-2xl bg-white border border-[#E6E2DA] p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-[#1A1815]">{tr('Acompanhamento de clientes', 'Client retention')}</h2>
            <p className="text-sm text-[#635E54] mt-1">{tr('Encontra quem pode precisar de uma nova marcação. Não são enviadas mensagens automaticamente.', 'Find clients who might need a follow-up. No messages are sent automatically.')}</p>
          </div>
          <Button onClick={refreshReport} disabled={loadingReport || !!saving} variant="outline" className="rounded-full h-11 w-full sm:w-auto shrink-0 border-[#E6E2DA]">
            <RefreshCw className={`w-4 h-4 mr-2 ${loadingReport ? 'animate-spin' : ''}`} /> {tr('Atualizar análise', 'Refresh')}
          </Button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mt-4">
          {[
            {label: tr('Prazo ultrapassado', 'Overdue'),value:(report.clients || []).filter(c => !c.contact?.contacted && c.overdue?.length).length, tone:'text-red-700',filter:'overdue'},
            {label: tr(`Inativos (+${report.inactivity_days} dias)`, `Inactive (${report.inactivity_days}+ days)`),value:(report.clients || []).filter(c => !c.contact?.contacted && c.inactive).length,tone:'text-amber-800',filter:'inactive'},
            {label: tr('Nunca marcaram', 'Never booked'),value:(report.clients || []).filter(c => !c.contact?.contacted && c.never_booked).length,tone:'text-[#1A1815]',filter:'never'},
            {label: tr('Sem visita anterior', 'No past visit'),value:(report.clients || []).filter(c => !c.contact?.contacted && c.without_visit).length,tone:'text-[#635E54]',filter:'without_visit'},
          ].map(card => (
            <button type="button" onClick={() => setFilter(card.filter)} key={card.filter}
              className={`min-w-0 text-left border rounded-xl p-3.5 sm:p-4 transition-colors min-h-[92px] ${filter === card.filter ? 'border-[#BFA05F] bg-[#FBF8F1]' : 'border-[#E6E2DA] bg-[#F9F8F6] hover:border-[#BFA05F]/60'}`}>
              <p className={`text-2xl font-bold ${card.tone}`}>{card.value || 0}</p>
              <p className="text-xs leading-4 mt-1 text-[#635E54]">{card.label}</p>
            </button>
          ))}
        </div>
        <p className="text-xs leading-relaxed text-[#7C746A] mt-3">{tr('Uma cliente pode estar simultaneamente inativa e ter prazos ultrapassados. Clientes com marcações futuras não contam como inativos.', 'A client can be inactive and overdue at the same time. Clients with upcoming bookings are not classified as inactive.')}</p>
      </div>

      {isAdmin && <div className="bg-white border border-[#E6E2DA] rounded-2xl overflow-hidden">
        <button type="button" aria-expanded={rulesOpen} onClick={() => setRulesOpen(!rulesOpen)}
          className="w-full min-h-14 flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-[#FBFAF7]">
          <span className="flex items-center gap-2 min-w-0"><Settings className="w-5 h-5 shrink-0 text-[#BFA05F]"/><span className="font-medium text-[#1A1815] text-sm sm:text-base">{tr('Definir prazos por serviço', 'Set service return deadlines')}</span></span>
          <ChevronDown className={`w-5 h-5 shrink-0 text-[#635E54] transition-transform ${rulesOpen ? 'rotate-180' : ''}`}/>
        </button>
        {rulesOpen && <div className="p-4 border-t border-[#E6E2DA] space-y-5">
          <p className="text-sm text-[#635E54]">{tr('Exemplo: unhas → 21 dias. Sem prazo = não gera alerta. Nenhum prazo de serviço é definido automaticamente.', 'Example: nails → 21 days. An empty deadline disables alerts. No service deadline is set automatically.')}</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end rounded-xl p-3 bg-[#F9F8F6] border border-[#E6E2DA]">
            <div className="flex-1 min-w-0">
              <label htmlFor="retention-inactivity" className="text-sm font-medium text-[#1A1815] block mb-1">{tr('Considerar cliente inativo após', 'Mark client inactive after')}</label>
              <div className="flex items-center gap-2"><Input id="retention-inactivity" type="number" min="1" max="730" inputMode="numeric" value={inactiveDraft} onChange={e=>setInactiveDraft(e.target.value)} className="h-11 text-base max-w-[130px]"/><span className="text-sm text-[#635E54]">{tr('dias', 'days')}</span></div>
            </div>
            <Button disabled={!!saving || Number(inactiveDraft) === report.inactivity_days} onClick={saveInactivity} className="h-11 w-full sm:w-auto rounded-full bg-[#1A1815] text-white hover:bg-[#BFA05F]">{tr('Guardar', 'Save')}</Button>
          </div>
          <div>
            <label htmlFor="retention-service-search" className="block text-sm font-medium text-[#1A1815] mb-2">{tr('Serviços e prazos', 'Services and return deadlines')}</label>
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29A]"/><Input id="retention-service-search" value={ruleQuery} onChange={e=>setRuleQuery(e.target.value)} placeholder={tr('Encontrar serviço…','Find service…')} className="h-11 pl-10 text-base" /></div>
          </div>
          <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1" style={{ WebkitOverflowScrolling: 'touch' }}>
            {visibleRules.map(s => {
              const draft = ruleDraft[s.service_id] ?? '';
              const savedValue = s.return_days == null ? '' : String(s.return_days);
              return <div key={s.service_id} className="rounded-xl border border-[#E6E2DA] p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="min-w-0 flex-1"><p className="font-medium text-sm text-[#1A1815] break-words">{s.name_pt}</p><p className="text-xs text-[#817A70]">{!s.is_active ? tr('Serviço inativo · ', 'Inactive service · ') : ''}{s.return_days ? tr(`Prazo atual: ${s.return_days} dias`,`Current: ${s.return_days} days`) : tr('Sem prazo definido', 'No deadline')}</p></div>
                <div className="flex gap-2 items-center min-w-0">
                  <Input type="number" min="1" max="730" inputMode="numeric" aria-label={`${tr('Prazo de regresso:', 'Return period:')} ${s.name_pt}`} placeholder={tr('Sem prazo', 'None')} value={draft} onChange={e=>setRuleDraft(prev=>({...prev,[s.service_id]:e.target.value}))} className="w-28 sm:w-32 min-w-0 h-11 text-base" />
                  <span className="text-sm text-[#635E54]">{tr('dias','days')}</span>
                  <Button onClick={()=>saveRule(s)} disabled={!!saving || draft === savedValue} className="shrink-0 h-11 px-4 bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full">{saving===s.service_id ? <Loader2 className="w-4 h-4 animate-spin"/> : tr('Guardar','Save')}</Button>
                </div>
              </div>;
            })}
            {!visibleRules.length && <p className="text-sm text-center text-[#817A70] py-5">{tr('Nenhum serviço encontrado.', 'No services found.')}</p>}
          </div>
        </div>}
      </div>}

      <div className="bg-white rounded-2xl border border-[#E6E2DA] p-4 sm:p-5 space-y-3">
        <label htmlFor="retention-client-search" className="text-sm font-medium text-[#1A1815]">{tr('Pesquisar clientes', 'Find clients')}</label>
        <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29A]"/><Input id="retention-client-search" type="search" value={query} onChange={e=>setQuery(e.target.value)} placeholder={tr('Nome, email ou telemóvel…','Name, email or phone…')} className="h-12 pl-10 text-base rounded-xl" /></div>
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" style={{ WebkitOverflowScrolling: 'touch' }} role="group" aria-label={tr('Filtrar estados', 'Filter status')}>
          {filters.map(([key,label,count])=><button type="button" key={key} onClick={()=>setFilter(key)} aria-pressed={filter===key}
            className={`rounded-full whitespace-nowrap px-4 min-h-11 text-sm font-medium border shrink-0 ${filter===key?'border-[#BFA05F] bg-[#BFA05F] text-white':'bg-white text-[#635E54] border-[#E6E2DA]'}`}>{label} <span className="opacity-80">{count}</span></button>)}
        </div>
        <p className="text-xs text-[#817A70]" aria-live="polite">{tr(`${matches.length} cliente(s) encontrado(s)`, `${matches.length} client(s) found`)}</p>
        <p className="text-xs text-[#817A70] leading-relaxed">{tr('As clientes já contactadas saem das filas de contacto, mas continuam na aba «Já contactadas» e em «Todos». O registo é partilhado com a equipa.', 'Contacted clients leave the action queues but remain under Contacted and All. The record is shared with the team.')}</p>
      </div>
      <div className="space-y-3">
        {matches.slice(0,limit).map(c => {
          const phone = waPhone(c.phone);
          const emailOK = validContactEmail(c.email);
          return <article key={c.user_id} className="bg-white border border-[#E6E2DA] rounded-2xl p-4 sm:p-5 space-y-3" data-testid="retention-client-card">
            <div className="flex justify-between gap-3 items-start">
              <div className="min-w-0"><h3 className="font-semibold text-[#1A1815] text-base break-words">{c.name}</h3><p className="text-xs text-[#817A70] break-all mt-1">{c.email && !c.email.includes('@temp.lousway') ? c.email : tr('Sem email', 'No email')}</p><p className="text-xs text-[#817A70]">{c.phone || tr('Sem telemóvel','No phone')}</p></div>
              <span className="shrink-0 bg-[#F9F8F6] rounded-full px-2.5 py-1 text-xs text-[#635E54]">{c.total_bookings} {tr('marcações','bookings')}</span>
            </div>
            <div className="flex gap-1.5 flex-wrap text-xs font-medium">
              {c.overdue?.length>0 && <span className="bg-red-50 text-red-700 border border-red-100 rounded-full px-2.5 py-1">{tr('Prazo ultrapassado','Overdue')}</span>}
              {c.inactive && <span className="bg-amber-50 text-amber-800 border border-amber-100 rounded-full px-2.5 py-1">{tr('Inativo','Inactive')}</span>}
              {c.never_booked && <span className="bg-slate-100 text-slate-700 rounded-full px-2.5 py-1">{tr('Nunca marcou','Never booked')}</span>}
              {c.without_visit && <span className="bg-slate-100 text-slate-700 rounded-full px-2.5 py-1">{tr('Sem visita anterior','No past visit')}</span>}
              {c.has_future && <span className="bg-green-50 text-green-800 border border-green-100 rounded-full px-2.5 py-1">{tr('Já tem marcação futura','Upcoming booking')}</span>}
              {c.contact?.contacted && <span className="bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2.5 py-1 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />{tr('Já contactada','Contacted')}</span>}
            </div>
            {c.last_booking && <p className="text-sm text-[#635E54]">{tr('Última marcação passada:','Last past booking:')} <strong className="text-[#1A1815]">{returnDate(c.last_booking,language)}</strong> <span className="text-xs">({c.days_since_last_booking} {tr('dias','days')})</span></p>}
            {c.overdue?.length>0 && <div className="rounded-xl border border-red-100 bg-[#FFF9F7] p-3 space-y-2">
              {c.overdue.map(o=><div className="text-sm" key={o.service_id}><p className="font-semibold text-[#9F352D] break-words">{o.service_name}: {o.days_overdue} {tr('dia(s) de atraso','day(s) overdue')}</p><p className="text-xs text-[#635E54] mt-0.5">{tr('Regresso previsto','Expected return')}: {returnDate(o.due_date,language)} · {tr('Prazo','Period')}: {o.return_days} {tr('dias','days')}</p></div>)}
            </div>}
            {c.contact?.contacted && <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 px-3 py-2 text-xs text-emerald-900 leading-relaxed">
              {tr('Contactada por', 'Contacted by')} <strong>{c.contact.contacted_by || tr('Equipa', 'Team')}</strong> · {returnDate(c.contact.contacted_at, language)} · {contactMethodLabel(c.contact.method, language)}
            </div>}
            {!c.last_booking && <p className="text-sm text-[#635E54]">{c.never_booked ? tr('Cliente registada, sem qualquer marcação.', 'Registered client with no bookings.') : tr('Não existem marcações passadas não canceladas.', 'No past non-cancelled bookings.')}</p>}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 pt-2 border-t border-[#F0EBE2]">
              <Button type="button" variant="outline" onClick={()=>onViewHistory(c)} className="h-11 rounded-full text-sm col-span-2 sm:col-span-1"><Calendar className="w-4 h-4 mr-2"/>{tr('Ver histórico','History')}</Button>
              <Button type="button" variant="outline" onClick={()=>onEditClient(c)} className="h-11 rounded-full text-sm col-span-2 sm:col-span-1"><Edit className="w-4 h-4 mr-2"/>{tr('Editar dados','Edit details')}</Button>
              {phone && <a href={`https://wa.me/${phone}`} target="_blank" rel="noopener noreferrer" onClick={() => startExternalContact(c, 'whatsapp')} className="h-11 rounded-full border border-[#E6E2DA] inline-flex justify-center items-center gap-2 px-4 text-sm font-medium text-[#1A1815]"><MessageCircle className="w-4 h-4"/>WhatsApp</a>}
              {emailOK && <a href={`mailto:${c.email}`} onClick={() => startExternalContact(c, 'email')} className="h-11 rounded-full border border-[#E6E2DA] inline-flex justify-center items-center gap-2 px-4 text-sm font-medium text-[#1A1815]"><Mail className="w-4 h-4"/>Email</a>}
              {c.contact?.contacted ? <Button type="button" variant="outline" disabled={contactSaving} onClick={() => saveContactStatus(c.user_id, false)} className="h-11 rounded-full text-sm col-span-2 sm:col-span-1 border-amber-300 text-amber-800"><RotateCcw className="w-4 h-4 mr-2" />{tr('Voltar a contactar', 'Follow up again')}</Button> :
              <Button type="button" variant="outline" onClick={() => promptManualContact(c)} className="h-11 rounded-full text-sm col-span-2 sm:col-span-1 border-emerald-200 text-emerald-800"><CheckCircle2 className="w-4 h-4 mr-2" />{tr('Marcar contactada', 'Mark contacted')}</Button>}
              {isAdmin && <button type="button" onClick={() => setDeleteTarget(c)}
                data-testid={`retention-delete-${c.user_id}`}
                aria-label={tr(`Excluir perfil de ${c.name}`, `Delete profile of ${c.name}`)}
                className="h-11 min-w-0 col-span-2 sm:col-span-1 rounded-full inline-flex items-center justify-center gap-2 px-4 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600">
                <Trash2 className="w-4 h-4 shrink-0" />{tr('Excluir', 'Delete')}
              </button>}
            </div>
          </article>;
        })}
        {matches.length===0 && <div className="bg-white border border-[#E6E2DA] rounded-2xl p-8 text-center text-[#635E54] text-sm">{tr('Nenhuma cliente corresponde a este filtro.', 'No clients match this filter.')}</div>}
        {matches.length>limit && <Button onClick={()=>setLimit(n=>n+40)} variant="outline" className="h-12 w-full rounded-full border-[#BFA05F] text-[#1A1815]">{tr(`Mostrar mais (${matches.length-limit} restantes)`, `Show more (${matches.length-limit} left)`)}</Button>}
      </div>
      <p className="text-xs text-[#817A70] leading-relaxed px-1">{report.methodology} {tr('Os alertas apoiam a gestão, não confirmam que o atendimento ocorreu. Antes de contactar a cliente, verifica o histórico e as preferências de comunicação.', 'Alerts support planning but do not prove a visit happened. Check history and communication preferences before contacting the client.')}</p>
      <Dialog open={!!contactTarget} onOpenChange={open => { if (!open && !contactSaving) closeContactDialog(); }}>
        <DialogContent className="w-[calc(100vw-24px)] max-w-[440px] rounded-2xl overflow-y-auto" style={{ maxHeight: 'min(90dvh, 700px)', paddingBottom: 'max(20px, env(safe-area-inset-bottom, 0px))' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A1815] text-lg">
              <CheckCircle2 className="h-5 w-5 text-[#BFA05F] shrink-0" />
              {tr('Registar contacto', 'Record contact')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-[#1A1815] leading-relaxed">{contactTarget?.mode === 'after-link'
              ? tr(`Já contactaste ${contactTarget?.name || 'esta cliente'}?`, `Have you contacted ${contactTarget?.name || 'this client'}?`)
              : tr(`Queres marcar ${contactTarget?.name || 'esta cliente'} como contactada?`, `Mark ${contactTarget?.name || 'this client'} as contacted?`)}</p>
            <p className="text-xs text-[#635E54]">{tr('Só confirma depois de efetivamente enviares a mensagem ou realizares o contacto. A abertura do WhatsApp/email, por si só, não envia nem confirma nada.', 'Only confirm after actually sending a message or making contact. Opening WhatsApp/email does not send or confirm anything.')}</p>
            {contactTarget?.mode === 'manual' && <div className="space-y-1">
              <label htmlFor="retention-contact-method" className="text-xs text-[#635E54] font-medium">{tr('Canal usado', 'Contact method')}</label>
              <select id="retention-contact-method" value={contactMethod} onChange={e => setContactMethod(e.target.value)} className="w-full min-h-11 rounded-xl border border-[#E6E2DA] bg-white px-3 text-sm text-[#1A1815]">
                <option value="other">{tr('Outro / contacto manual', 'Other / manual contact')}</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
              </select>
            </div>}
          </div>
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <Button type="button" variant="outline" disabled={contactSaving} onClick={closeContactDialog} className="w-full sm:w-auto min-h-11 rounded-full">{tr('Não, ainda não', 'No, not yet')}</Button>
            <Button type="button" disabled={contactSaving} onClick={() => saveContactStatus(contactTarget.user_id, true, contactMethod)} className="w-full sm:w-auto min-h-11 rounded-full bg-[#1A1815] text-white hover:bg-[#BFA05F]">
              {contactSaving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {tr('Sim, já contactei', 'Yes, contacted')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {isAdmin && <Dialog open={!!deleteTarget} onOpenChange={open => { if (!open && !deleting) setDeleteTarget(null); }}>
        <DialogContent className="w-[calc(100vw-24px)] max-w-[440px] rounded-2xl overflow-y-auto"
          style={{ maxHeight: 'min(90dvh, 760px)', paddingBottom: 'max(24px, env(safe-area-inset-bottom, 0px))' }}>
          <DialogHeader>
            <DialogTitle className="flex gap-2 items-center text-lg text-red-700">
              <Trash2 className="h-5 w-5 shrink-0" />{tr('Excluir utilizador', 'Delete user')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 min-w-0">
            <div className="rounded-xl border border-[#E6E2DA] p-3 bg-[#F9F8F6]">
              <p className="font-semibold text-sm text-[#1A1815] break-words">{deleteTarget?.name}</p>
              <p className="text-xs text-[#635E54] break-all mt-1">{deleteTarget?.email}</p>
            </div>
            {(deleteTarget?.total_bookings || 0) > 0 ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 leading-relaxed" role="alert">
                {tr(`Este perfil tem ${deleteTarget.total_bookings} marcação(ões) associada(s). Para proteger o histórico e os pagamentos, não pode ser eliminado diretamente. É necessário associar primeiro essas marcações ao perfil correto.`, `This profile has ${deleteTarget.total_bookings} related booking(s). To protect appointment and payment records, it cannot be deleted directly. Reassign bookings to the correct profile first.`)}
              </div>
            ) : (
              <p className="text-sm text-[#635E54] leading-relaxed">
                {tr('Queres mesmo eliminar permanentemente este perfil? Esta ação não pode ser desfeita. O servidor também verifica se existem registos associados antes de permitir a eliminação.', 'Permanently delete this profile? This cannot be undone. The server also checks for associated records before deleting.')}
              </p>
            )}
          </div>
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <Button type="button" variant="outline" disabled={deleting} onClick={() => setDeleteTarget(null)} className="h-11 rounded-full w-full sm:w-auto">
              {tr('Voltar', 'Back')}
            </Button>
            {(deleteTarget?.total_bookings || 0) === 0 && (
              <Button type="button" onClick={confirmRetentionDelete} disabled={deleting}
                className="h-11 rounded-full w-full sm:w-auto bg-red-700 hover:bg-red-800 text-white">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {tr('Confirmar exclusão', 'Confirm deletion')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>}
    </section>
  );
};


const UserManagementPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [activeUserTab, setActiveUserTab] = useState(user?.role === 'staff' ? 'retention' : 'users');
  const [merging, setMerging] = useState(false);

  // Estados para Edição
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '' });

  // Estados para Histórico de Agendamentos
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [userAppointments, setUserAppointments] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedUserForHistory, setSelectedUserForHistory] = useState(null);

  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff';
  const canManageClients = isAdmin || isStaff;

  useEffect(() => {
    if (!user) return;
    if (!canManageClients) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
    // Recarregar dados apenas se o cargo mudar, não a cada atualização da sessão.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, canManageClients, navigate]);

  useEffect(() => {
    if (isStaff) setActiveUserTab('retention');
  }, [isStaff]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/users');
      
      if (res.data && Array.isArray(res.data)) {
        const sortedUsers = [...res.data].sort((a, b) => {
          const nameA = a.name ? String(a.name).toUpperCase() : "";
          const nameB = b.name ? String(b.name).toUpperCase() : "";
          return nameA.localeCompare(nameB);
        });
        setUsers(sortedUsers);
      } else {
        setUsers(res.data);
      }
      
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(language === 'pt' ? 'Erro ao carregar utilizadores' : 'Error loading users');
    } finally {
      setLoading(false);
    }
  };

  // Nº de emails que aparecem em mais do que um perfil (ignora convidados sem email real)
  const duplicateEmailCount = (() => {
    const counts = {};
    (users || []).forEach(u => {
      const e = (u.email || '').trim().toLowerCase();
      if (!e || e.endsWith('@temp.lousway')) return;
      counts[e] = (counts[e] || 0) + 1;
    });
    return Object.values(counts).filter(c => c > 1).length;
  })();

  const handleMergeDuplicates = async () => {
    if (!isAdmin) return;
    const confirmMsg = language === 'pt'
      ? `Isto vai unir todos os clientes com o mesmo email num único perfil, movendo os agendamentos para o perfil principal. Esta ação não pode ser desfeita. Continuar?`
      : `This will merge all clients sharing the same email into a single profile, moving their appointments to the main profile. This cannot be undone. Continue?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      setMerging(true);
      const res = await api.post('/admin/users/merge-duplicates');
      const d = res.data || {};
      if ((d.merged_groups || 0) === 0) {
        toast.success(language === 'pt' ? 'Nenhum cliente duplicado encontrado.' : 'No duplicate clients found.');
      } else {
        toast.success(
          language === 'pt'
            ? `Consolidação concluída: ${d.merged_groups} email(s) unidos, ${d.removed_users || 0} perfil(is) removido(s), ${d.reassigned_bookings || 0} agendamento(s) movido(s).`
            : `Merge complete: ${d.merged_groups} email(s) merged, ${d.removed_users || 0} profile(s) removed, ${d.reassigned_bookings || 0} booking(s) moved.`
        );
      }
      await fetchUsers();
    } catch (error) {
      console.error('Merge duplicates error:', error);
      toast.error(language === 'pt' ? 'Erro ao unir duplicados' : 'Error merging duplicates');
    } finally {
      setMerging(false);
    }
  };

  const fetchUserHistory = async (u) => {
    try {
      setLoadingHistory(true);
      setSelectedUserForHistory(u);
      setIsHistoryModalOpen(true);
      
      const res = await api.get(`/admin/users/${u.user_id}/appointments`);
      setUserAppointments(res.data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error(language === 'pt' ? 'Erro ao carregar histórico' : 'Error loading history');
      setUserAppointments([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const openEditModal = (u) => {
    setEditingUser(u);
    setEditForm({
      name: u.name || '',
      email: u.email || '',
      phone: u.phone || ''
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = async () => {
    try {
      await api.put(`/admin/users/${editingUser.user_id}`, editForm);
      toast.success(language === 'pt' ? 'Utilizador atualizado com sucesso!' : 'User updated successfully!');
      setIsEditModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || (language === 'pt' ? 'Erro ao atualizar dados' : 'Error updating data'));
    }
  };

  const updateUserRole = async (userId, newRole) => {
    if (!isAdmin) return;
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      toast.success(language === 'pt' ? `Papel atualizado para ${newRole}` : `Role updated to ${newRole}`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error(language === 'pt' ? 'Erro ao atualizar papel' : 'Error updating role');
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!isAdmin) return;
    if (!window.confirm(
      language === 'pt' 
        ? `Tem a certeza que deseja excluir o utilizador "${userName}"? Esta ação é irreversível.`
        : `Are you sure you want to delete user "${userName}"? This action cannot be undone.`
    )) {
      return;
    }
    
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success(language === 'pt' ? 'Utilizador excluído com sucesso' : 'User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || (language === 'pt' ? 'Erro ao excluir utilizador' : 'Error deleting user'));
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-red-100 text-red-700',
      staff: 'bg-blue-100 text-blue-700',
      client: 'bg-gray-100 text-gray-700',
    };
    const labels = {
      admin: language === 'pt' ? 'Admin' : 'Admin',
      staff: language === 'pt' ? 'Colab.' : 'Staff',
      client: language === 'pt' ? 'Cliente' : 'Client',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[role] || styles.client}`}>
        {labels[role] || role}
      </span>
    );
  };

  const filteredUsers = users.filter(u => {
    if (!isAdmin && u.role !== 'client') return false;
    const matchesSearch = 
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.phone?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    return matchesSearch && matchesRole;
  });

  if (loading && users.length === 0) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#BFA05F] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] pt-20 pb-24" style={{ paddingBottom: "max(96px, env(safe-area-inset-bottom, 0px))" }} data-testid="user-management">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1815]">
              {language === 'pt' ? 'Gestão de Utilizadores' : 'User Management'}
            </h1>
            <p className="text-sm text-[#635E54] mt-1">
              {isAdmin ? (language === 'pt' ? 'Gerir papéis e permissões' : 'Manage roles and permissions') : (language === 'pt' ? 'Acompanhar e recuperar clientes inativas' : 'Follow up with inactive clients')}
            </p>
          </div>
          {isAdmin && activeUserTab === 'users' && <Button
            data-testid="merge-duplicates-btn"
            onClick={handleMergeDuplicates}
            disabled={merging}
            className="rounded-full bg-[#BFA05F] text-white hover:bg-[#9E8246] shrink-0"
          >
            {merging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GitMerge className="w-4 h-4 mr-2" />}
            {language === 'pt' ? 'Unir Duplicados' : 'Merge Duplicates'}
            {duplicateEmailCount > 0 && (
              <span data-testid="duplicate-count-badge" className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-white/25 text-xs font-semibold">
                {duplicateEmailCount}
              </span>
            )}
          </Button>}
        </div>

        <div className="grid grid-cols-2 gap-2 mb-5" role="tablist" aria-label={language === 'pt' ? 'Secções de utilizadores' : 'User management sections'}>
          <button type="button" role="tab" aria-selected={activeUserTab === 'users'} onClick={() => setActiveUserTab('users')}
            className={`min-h-12 rounded-xl text-sm font-semibold px-3 flex items-center justify-center gap-2 ${activeUserTab === 'users' ? 'bg-[#1A1815] text-white' : 'bg-white text-[#635E54] border border-[#E6E2DA]'}`}>
            <Users className="w-4 h-4 shrink-0" /> {language === 'pt' ? 'Utilizadores' : 'Users'}
          </button>
          <button type="button" role="tab" aria-selected={activeUserTab === 'retention'} onClick={() => setActiveUserTab('retention')}
            className={`min-h-12 rounded-xl text-sm font-semibold px-3 flex items-center justify-center gap-2 ${activeUserTab === 'retention' ? 'bg-[#1A1815] text-white' : 'bg-white text-[#635E54] border border-[#E6E2DA]'}`}>
            <Calendar className="w-4 h-4 shrink-0" /> {language === 'pt' ? 'Acompanhamento' : 'Retention'}
          </button>
        </div>
        {activeUserTab === 'retention' ? <ClientRetentionPanel language={language} onViewHistory={fetchUserHistory} onEditClient={openEditModal} onUserDeleted={fetchUsers} isAdmin={isAdmin} /> : <>
        {/* Stats Cards */}
        {isAdmin && <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
          <div className="bg-white p-3 sm:p-4 rounded-xl border border-[#E6E2DA] text-center">
            <p className="text-xl sm:text-2xl font-bold text-[#1A1815]">{users.filter(u => u.role === 'admin').length}</p>
            <p className="text-[10px] sm:text-xs text-[#A8A29A]">{language === 'pt' ? 'Admins' : 'Admins'}</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-xl border border-[#E6E2DA] text-center">
            <p className="text-xl sm:text-2xl font-bold text-[#1A1815]">{users.filter(u => u.role === 'staff').length}</p>
            <p className="text-[10px] sm:text-xs text-[#A8A29A]">{language === 'pt' ? 'Colab.' : 'Staff'}</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-xl border border-[#E6E2DA] text-center">
            <p className="text-xl sm:text-2xl font-bold text-[#1A1815]">{users.filter(u => u.role === 'client').length}</p>
            <p className="text-[10px] sm:text-xs text-[#A8A29A]">{language === 'pt' ? 'Clientes' : 'Clients'}</p>
          </div>
        </div>}

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29A]" />
          <Input
            type="text"
            placeholder={language === 'pt' ? 'Pesquisar...' : 'Search...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-full border-[#E6E2DA]"
          />
        </div>

        {/* Filter Pills */}
        {isAdmin && <div className="flex gap-2 mb-4 overflow-x-auto pb-2 -mx-4 px-4">
          {['all', 'admin', 'staff', 'client'].map((role) => (
            <Button
              key={role}
              variant={filterRole === role ? 'default' : 'outline'}
              onClick={() => setFilterRole(role)}
              className={`rounded-full whitespace-nowrap text-xs ${filterRole === role ? 'bg-[#BFA05F]' : ''}`}
              size="sm"
            >
              {role === 'all' ? (language === 'pt' ? 'Todos' : 'All') : 
               role === 'admin' ? 'Admins' :
               role === 'staff' ? (language === 'pt' ? 'Colab.' : 'Staff') :
               (language === 'pt' ? 'Clientes' : 'Clients')}
            </Button>
          ))}
        </div>}

        {/* Users List */}
        <div className="space-y-3">
          {filteredUsers.map((u) => (
            <div 
              key={u.user_id} 
              className="bg-white rounded-xl border border-[#E6E2DA] p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {u.picture ? (
                    <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#BFA05F]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#BFA05F] font-medium text-sm">{u.name?.charAt(0) || '?'}</span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-[#1A1815] text-sm truncate">{u.name}</p>
                      {getRoleBadge(u.role)}
                    </div>
                    <p className="text-xs text-[#A8A29A] truncate">{u.email}</p>
                  </div>
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEditModal(u)}>
                      <Edit className="w-4 h-4 mr-2 text-amber-500" />
                      {language === 'pt' ? 'Editar Dados' : 'Edit Details'}
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => fetchUserHistory(u)}>
                      <Calendar className="w-4 h-4 mr-2 text-[#BFA05F]" />
                      {language === 'pt' ? 'Ver Histórico' : 'View History'}
                    </DropdownMenuItem>

                    {isAdmin && <>
                    <DropdownMenuSeparator />

                    <DropdownMenuItem 
                      onClick={() => updateUserRole(u.user_id, 'client')}
                      disabled={u.role === 'client'}
                    >
                      <User className="w-4 h-4 mr-2 text-gray-500" />
                      {language === 'pt' ? 'Tornar Cliente' : 'Make Client'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => updateUserRole(u.user_id, 'staff')}
                      disabled={u.role === 'staff'}
                    >
                      <Briefcase className="w-4 h-4 mr-2 text-blue-500" />
                      {language === 'pt' ? 'Tornar Colaborador' : 'Make Staff'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => updateUserRole(u.user_id, 'admin')}
                      disabled={u.role === 'admin'}
                    >
                      <Shield className="w-4 h-4 mr-2 text-red-500" />
                      {language === 'pt' ? 'Tornar Admin' : 'Make Admin'}
                    </DropdownMenuItem>
                    {u.role !== 'admin' && u.user_id !== user?.user_id && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => deleteUser(u.user_id, u.name)}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {language === 'pt' ? 'Excluir Utilizador' : 'Delete User'}
                        </DropdownMenuItem>
                      </>
                    )}
                    </>}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              <div className="mt-3 pt-3 border-t border-[#E6E2DA] flex justify-between text-xs text-[#A8A29A]">
                <span>{u.phone || (language === 'pt' ? 'Sem telefone' : 'No phone')}</span>
                <span>{u.loyalty_points || 0} {language === 'pt' ? 'pontos' : 'points'}</span>
              </div>
            </div>
          ))}
          
          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-[#A8A29A]">
              {language === 'pt' ? 'Nenhum utilizador encontrado.' : 'No users found.'}
            </div>
          )}
        </div>

        </>}

        {/* MODAL DE EDIÇÃO */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="sm:max-w-[425px] overflow-y-auto" style={{ maxHeight: "min(90dvh, 820px)", paddingBottom: "max(20px, env(safe-area-inset-bottom, 0px))" }}>
            <DialogHeader>
              <DialogTitle>
                {language === 'pt' ? 'Editar Utilizador' : 'Edit User'}
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{language === 'pt' ? 'Nome' : 'Name'}</label>
                <Input 
                  value={editForm.name} 
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})} 
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{language === 'pt' ? 'Email' : 'Email'}</label>
                <Input 
                  type="email"
                  value={editForm.email} 
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})} 
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{language === 'pt' ? 'Telemóvel' : 'Phone'}</label>
                <Input 
                  type="tel"
                  value={editForm.phone} 
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})} 
                />
              </div>
            </div>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)} className="rounded-full">
                {language === 'pt' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button onClick={handleUpdateUser} className="bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full">
                {language === 'pt' ? 'Salvar Alterações' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* MODAL DE HISTÓRICO DE AGENDAMENTOS */}
        <Dialog open={isHistoryModalOpen} onOpenChange={setIsHistoryModalOpen}>
          <DialogContent className="sm:max-w-[500px] overflow-y-auto" style={{ maxHeight: "min(88dvh, 820px)", paddingBottom: "max(20px, env(safe-area-inset-bottom, 0px))" }}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#BFA05F]" />
                {language === 'pt' ? `Histórico: ${selectedUserForHistory?.name}` : `History: ${selectedUserForHistory?.name}`}
              </DialogTitle>
            </DialogHeader>

            <div className="py-4">
              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#BFA05F]"></div>
                </div>
              ) : userAppointments.length > 0 ? (
                <div className="space-y-3">
                  {userAppointments.map((appo, idx) => (
                    <div key={idx} className="p-3 rounded-lg border border-[#E6E2DA] bg-[#F9F8F6]">
                      <div className="flex justify-between items-start mb-1">
                        <p className="font-medium text-sm text-[#1A1815]">{appo.service_name}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                          appo.status === 'completed' ? 'bg-green-100 text-green-700' : 
                          appo.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {appo.status}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-[#A8A29A]">
                        <span>{appo.date ? new Date(appo.date).toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-US') : ''}</span>
                        <span>{appo.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-[#A8A29A] text-sm">
                  {language === 'pt' ? 'Nenhum agendamento encontrado.' : 'No appointments found.'}
                </p>
              )}
            </div>
            
            <DialogFooter>
              <Button onClick={() => setIsHistoryModalOpen(false)} className="w-full rounded-full">
                {language === 'pt' ? 'Fechar' : 'Close'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
};

export default UserManagementPage;